import { useState, useEffect, useRef } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Calendar } from "lucide-react";
import { Input } from "ui/input";
import { toast } from "sonner";
import { format } from "date-fns";

export function CustomDayPicker({
  value,
  onChange,
  disabledDates = [],
  cutoffDates = [],
  blockedDateReasons = [],
  highlightRange,
  mode = "default",
  placeholder = "Select date",
  disabled = false,
  isDisabled = false,
  operatorExpiryDate,
  minDate,
  month,
  onMonthChange,
}: {
  value?: string;
  onChange: (date: Date) => void;
  disabledDates?: Date[];
  cutoffDates?: Date[];
  blockedDateReasons?: {
    date: Date;
    label: string;
  }[];
  highlightRange?: { from?: string; to?: string };
  mode?: "default" | "range";
  placeholder?: string;
  disabled?: boolean | ((date: Date) => boolean);
  isDisabled?: boolean;
  operatorExpiryDate?: Date;
  minDate?: Date;
  month?: Date;
  onMonthChange?: (month: Date) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const parseDate = (val?: string) => {
    if (!val) return undefined;
    const [y, m, d] = val.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const selectedDate = parseDate(value);

  const normalize = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const isSameDay = (d1: Date, d2: Date) =>
    normalize(d1).getTime() === normalize(d2).getTime();

  const normalizedDisabledDates = disabledDates.map(normalize);
  const normalizedCutoffDates = cutoffDates.map(normalize);

  const getBlockedDateLabel = (date: Date) => {
    const normalizedDate = normalize(date);

    const blockedDate = blockedDateReasons.find((item) =>
      isSameDay(item.date, normalizedDate),
    );

    return blockedDate?.label;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleOpen = () => {
    if (isDisabled) return;
    setOpen(true);
  };

  const isDisabledDate = (date: Date) => {
    const normalizedDate = normalize(date);

    if (minDate) {
      const min = normalize(minDate);

      if (normalizedDate < min) {
        return true;
      }
    }

    const isCutoffDate = normalizedCutoffDates.some((d) =>
      isSameDay(d, normalizedDate),
    );

    if (isCutoffDate) {
      return true;
    }

    const isCustomDisabled =
      typeof disabled === "function" ? disabled(normalizedDate) : false;

    if (operatorExpiryDate) {
      const expiry = normalize(operatorExpiryDate);

      if (normalizedDate > expiry) return true;
    }

    return isCustomDisabled;
  };

  const rangeFrom = parseDate(highlightRange?.from);
  const rangeTo = parseDate(highlightRange?.to);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={wrapperRef} className="relative w-full">
      <Input
        readOnly
        value={selectedDate ? format(selectedDate, "dd MMM yyyy") : ""}
        placeholder={placeholder}
        onClick={() => {
          if (isDisabled) return;
          setOpen((prev) => !prev);
        }}
        className={`!h-5 text-sm pr-10 border-none shadow-none bg-transparent focus:ring-0 focus:outline-none ${
          isDisabled
            ? "text-gray-500 cursor-not-allowed opacity-70"
            : "cursor-pointer"
        }`}
      />

      <div
        onClick={() => {
          if (isDisabled) return;
          setOpen((prev) => !prev);
        }}
        className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 ${
          isDisabled ? "pointer-events-none opacity-50" : "cursor-pointer"
        }`}
      >
        <Calendar className="w-4 h-4 text-gray-500" />
      </div>

      {open && !isDisabled && (
        <div className="absolute top-full left-0 z-50 mt-2 bg-white border rounded-xl shadow-xl p-3 w-max">
          <DayPicker
            mode="single"
            selected={selectedDate}
            month={month}
            onMonthChange={onMonthChange}
            onSelect={(date) => {
              if (!date || isDisabled) return;
              if (isDisabledDate(date)) return;

              if (operatorExpiryDate) {
                const selected = normalize(date);
                const expiry = normalize(operatorExpiryDate);

                if (selected > expiry) {
                  toast.error("Cannot select date beyond operator expiry");
                  return;
                }
              }

              if (mode === "default") {
                const normalizedDate = normalize(date);

                const isBlocked = normalizedDisabledDates.some((d) =>
                  isSameDay(d, normalizedDate),
                );

                if (isBlocked) {
                  toast.warning("Some ports are unavailable for this date");
                }
              }

              onChange(date);
              setOpen(false);
            }}
            disabled={isDisabledDate}
            components={{
              DayButton: ({ day, modifiers, ...props }) => {
                let tooltip = "";

                if (modifiers.cutoff) {
                  tooltip = "Cutoff block";
                } else if (modifiers.blocked) {
                  tooltip = getBlockedDateLabel(day.date) || "Port block";
                }

                return (
                  <button
                    {...props}
                    title={tooltip || undefined}
                    className={`${props.className || ""} relative z-[99999]`}
                  />
                );
              },
            }}
            modifiers={
              mode === "range"
                ? {
                    range_start: (date) => {
                      if (!rangeFrom) return false;
                      return (
                        normalize(date).getTime() ===
                        normalize(rangeFrom).getTime()
                      );
                    },

                    range_end: (date) => {
                      if (!rangeTo) return false;
                      return (
                        normalize(date).getTime() ===
                        normalize(rangeTo).getTime()
                      );
                    },

                    range_middle: (date) => {
                      if (!rangeFrom || !rangeTo) return false;

                      const t = normalize(date).getTime();
                      const start = normalize(rangeFrom).getTime();
                      const end = normalize(rangeTo).getTime();

                      return t >= start && t <= end;
                    },
                  }
                : {
                    blocked: (date) =>
                      normalizedDisabledDates.some((d) => isSameDay(d, date)),

                    cutoff: (date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);

                      // Today should remain blue, not orange.
                      if (isSameDay(date, today)) {
                        return false;
                      }

                      return normalizedCutoffDates.some((d) =>
                        isSameDay(d, date),
                      );
                    },
                  }
            }
            modifiersClassNames={
              mode === "range"
                ? {
                    range_start: "text-sky-600 font-semibold",
                    range_end: "text-sky-600 font-semibold",
                    range_middle: "text-sky-600 font-semibold",
                  }
                : {
                    blocked: "text-red-600 font-semibold",
                    cutoff: "text-orange-500 font-semibold",
                  }
            }
            classNames={{
              day_selected:
                "!bg-sky-600 !text-white hover:!bg-sky-600 focus:!bg-sky-600 rounded-full",

              nav_button: "!text-sky-600 hover:!text-sky-700 transition-colors",

              nav_button_previous: "!text-sky-600",
              nav_button_next: "!text-sky-600",

              day_today: "!text-sky-600 font-semibold",
            }}
          />
        </div>
      )}
    </div>
  );
}
